/**
 * The operational chain's screens are descriptors, like the masters: ONE register, ONE action dialog, and a
 * description per document of which view it reads, what it shows, and which commands act on it.
 *
 * Nothing here knows a business rule. A descriptor names an `api.*` command and the fields it takes; whether
 * the command is allowed — the permission at that site, who captures there (CD-63), the state of the
 * document — is decided by the database and reported in its own words.
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { FilterDef } from '../../ui/filters';

export type OpsRow = Record<string, unknown> & { id: string };

export type OpsField =
  | { key: string; label: string; kind: 'text' | 'scan' | 'date' | 'lines' | 'textarea'; required?: boolean; hint?: string }
  | { key: string; label: string; kind: 'grams' | 'integer' | 'celsius'; required?: boolean; hint?: string }
  | { key: string; label: string; kind: 'boolean'; hint?: string }
  | { key: string; label: string; kind: 'select'; options: readonly { value: string; label: string }[]; required?: boolean; hint?: string }
  | {
      key: string; label: string; kind: 'ref'; required?: boolean; hint?: string;
      view: string; labelColumns: string[]; valueColumn?: string;
      /** PostgREST equality filters, e.g. { kind: 'MAJOR_LOCUS' }. `in.(…)` style values are passed through. */
      where?: Record<string, string>;
      noStatus?: boolean;
    };

export type OpsValues = Record<string, string | boolean>;

export interface OpsAction {
  key: string;
  label: string;
  icon?: LucideIcon;
  /** Held anywhere → the button is offered. The command re-checks it at the site. */
  permission: string;
  rpc: string;
  scope: 'global' | 'row';
  available?: (row: OpsRow) => boolean;
  intro?: ReactNode;
  fields: OpsField[];
  initial?: (row: OpsRow | null) => OpsValues;
  /** Builds the RPC arguments. `key` is a stable idempotency key for this opening of the dialog. */
  params: (values: OpsValues, row: OpsRow | null, key: string) => Record<string, unknown>;
  /** Runs before params when the typed values need a lookup first (a product code to its id). */
  prepare?: (values: OpsValues) => Promise<OpsValues>;
  submitLabel?: string;
  /** What to tell the person afterwards. Warnings the command returned are appended. */
  success: (result: Record<string, unknown>) => string;
  /** Other registers whose data this changes. */
  invalidates?: string[];
}

export interface OpsColumn {
  key: string;
  header: string;
  width?: string;
  mono?: boolean;
  /** An admin-only code (a setting key, a command): shown, but not read as words (v7 Part D §9). */
  technical?: boolean;
  numeric?: boolean;
  optional?: boolean;
  render?: (row: OpsRow) => ReactNode;
}

export interface OpsDescriptor {
  id: string;
  title: string;
  subtitle: string;
  footer: string;
  viewPermission: string;
  view: string;
  select: string;
  rowKey?: string;
  searchColumns: string[];
  filters?: FilterDef[];
  /** Always applied, e.g. the transit screen is the shipments that have not been received. */
  fixed?: Record<string, string>;
  defaultSort: string;
  defaultSortDirection?: 'asc' | 'desc';
  columns: OpsColumn[];
  /** Ways to see the rows grouped (the owner, 2026-10-19: "month wise or customer wise … in a group by manner"):
   *  each group shows its count and the sum of `sums`, and opens to its rows. */
  groupBy?: { key: string; label: string; value?: (row: OpsRow) => string }[];
  sums?: { key: string; label: string }[];
  actions: OpsAction[];
  /** A row opens this page instead of the drawer: the record is managed there (the owner, 3 Nov 2026: the customer's one Agreement panel). */
  rowLink?: (row: OpsRow) => string | null;
  /** Whether any action here is a field-captured fact (CD-63), so the capture banner belongs on the page. */
  captures: boolean;
  detail?: {
    entityType: string;
    title: (row: OpsRow) => string;
    trace?: (row: OpsRow) => string | null;
    /** The id `entityType` is keyed by, where it is not the register's own row key. */
    entityId?: (row: OpsRow) => string | null;
    /** Photographs and PDFs can be attached to this kind of record (`core.attachment_targets`). */
    evidence?: boolean;
    /** Links to the records this one touches (`api.record_links`). On unless turned off. */
    links?: boolean;
    /** The audit timeline of the record. On unless turned off — the audit log has no timeline of its own rows. */
    timeline?: boolean;
    /** Anything else the drawer should show for this kind of record. */
    extra?: (row: OpsRow) => ReactNode;
  };
}

/** Grams as kilograms, to the gram. The ledger is grams; people read kilograms. */
export function kg(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const grams = Number(value);
  if (!Number.isFinite(grams)) return null;
  return `${(grams / 1000).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;
}

/** Tenths of a degree, with a real minus sign. */
export function celsius(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const c10 = Number(value);
  if (!Number.isFinite(c10)) return null;
  return `${c10 < 0 ? '−' : ''}${(Math.abs(c10) / 10).toFixed(1)} °C`;
}

export function when(value: unknown): string | null {
  if (!value) return null;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

/** "fat_pct=4.2" / "urea=NEG", one per line → the observations the test command takes. */
export function parseObservations(text: string): { parameter: string; value?: number; code?: string }[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const at = line.indexOf('=');
      const parameter = (at < 0 ? line : line.slice(0, at)).trim();
      const raw = at < 0 ? '' : line.slice(at + 1).trim();
      const numeric = raw !== '' && /^-?\d+(\.\d+)?$/.test(raw);
      return numeric ? { parameter, value: Number(raw) } : { parameter, code: raw.toUpperCase() };
    });
}

export type BarrelLine = ({ scan: string } | { barrel_id: string }) & { seal_no?: string; dispatch_temperature_c10?: number };

/**
 * One barrel per line: a scanned QR payload, or a barrel's uuid when it has no label — optionally followed by
 * the seal it leaves under and its temperature, separated by commas: `DSG-BAR-000012-4, S-1001, 3.8`.
 */
export function parseBarrelLines(text: string): BarrelLine[] {
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [identifier = '', seal = '', temperature = ''] = line.split(',').map((part) => part.trim());
      const barrel: BarrelLine = uuid.test(identifier) ? { barrel_id: identifier } : { scan: identifier.toUpperCase() };
      if (seal) barrel.seal_no = seal;
      if (/^-?\d+(\.\d)?$/.test(temperature)) barrel.dispatch_temperature_c10 = Math.round(Number(temperature) * 10);
      return barrel;
    });
}
